import React, { useMemo } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { useGetNotificationStatsQuery } from '../store/api/notificationApi';

const UserNotificationBell = () => {
  const history = useHistory();
  // Fallback to stats query if notifications query doesn't include stats
  const { 
    data: statsResponse,
    isLoading: isLoadingStats,
    error: notificationError
  } = useGetNotificationStatsQuery(undefined, {
    pollingInterval: 60000,
  });

  // Extract unread count from either response
  const stats = useMemo(() => statsResponse?.stats, [statsResponse]);
  const unreadCount = useMemo(() => parseInt(stats?.unread) || 0, [stats]);

  // Handle manual refresh on click (optional enhancement)
  const handleBellClick = () => {
    // Optionally refetch before navigating for most up-to-date data
    history.push('/user/notifications');
  };

  return (
    <Button
      variant="link"
      onClick={handleBellClick}
      className="notification-bell position-relative p-2"
      style={{ 
        border: 'none',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        fontSize: '1.2rem',
        color: '#ffffff',
        transition: 'all 0.3s ease',
        minWidth: '50px',
        minHeight: '45px'
      }}
      onMouseEnter={(e) => {
        e.target.style.background = 'rgba(255, 255, 255, 0.2)';
        e.target.style.transform = 'scale(1.05)';
      }}
      onMouseLeave={(e) => {
        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
        e.target.style.transform = 'scale(1)';
      }}
      disabled={isLoadingStats}
      title={
        notificationError 
          ? 'Error loading notifications' 
          : `You have ${unreadCount} unread notifications`
      }
    >
      <i 
        className={`bi ${notificationError ? 'bi-bell-slash' : 'bi-bell'}`}
        style={{ 
          fontSize: '1.6rem',
          color: notificationError ? '#ffcccc' : '#ffffff',
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
        }}
      ></i>
      
      {/* Unread count badge */}
      {unreadCount > 0 && !notificationError && (
        <Badge 
          bg="warning" 
          pill 
          className="position-absolute top-0 start-100 translate-middle"
          style={{ 
            fontSize: '0.75rem',
            fontWeight: 'bold',
            color: '#000',
            border: '2px solid #ffffff',
            minWidth: '22px',
            minHeight: '22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none'
          }}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </Badge>
      )}
      
      {/* Error indicator */}
      {notificationError && (
        <Badge 
          bg="danger" 
          pill 
          className="position-absolute top-0 start-100 translate-middle"
          style={{ 
            fontSize: '0.6rem',
            border: '2px solid #ffffff',
            minWidth: '18px',
            minHeight: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Error loading notifications"
        >
          !
        </Badge>
      )}
      
      {/* Loading indicator */}
      {isLoadingStats && (
        <div 
          className="position-absolute top-50 start-50 translate-middle"
          style={{ fontSize: '0.8rem' }}
        >
          <i 
            className="bi bi-arrow-repeat" 
            style={{ 
              animation: 'spin 1s linear infinite',
              color: '#ffffff'
            }}
          ></i>
        </div>
      )}
      
      {/* Add CSS animation styles */}
      <style >{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
      `}</style>
    </Button>
  );
};

export default UserNotificationBell; 